import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import config from "../../config";

const POLL_MS = 60 * 1000; // check for new notifications every minute

function getToken(reduxToken) {
  return reduxToken || localStorage.getItem("token") || sessionStorage.getItem("token");
}

function timeAgo(iso) {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// Inline SVG icons (stroke = currentColor) so the bell always shows, even when
// the Bootstrap Icons font isn't loaded on the site.
const svgProps = (size) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
});

const BellIcon = ({ size = 22 }) => (
  <svg {...svgProps(size)}>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const CalendarIcon = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const AlarmIcon = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <circle cx="12" cy="13" r="8" />
    <polyline points="12 9 12 13 14 15" />
    <path d="M5 3 2 6" />
    <path d="m22 6-3-3" />
  </svg>
);

const UserNotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const reduxToken = useSelector((state) => state.user?.token);

  const request = useCallback(
    async (path, options = {}) => {
      const token = getToken(reduxToken);
      if (!token) throw new Error("Not signed in");
      const res = await fetch(`${config.baseURL}/api/user-notifications${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
      return body;
    },
    [reduxToken]
  );

  const load = useCallback(async () => {
    try {
      const body = await request("");
      setItems(Array.isArray(body.data) ? body.data : []);
      setUnread(Number(body.unread_count) || 0);
    } catch (err) {
      // Silent: a failed poll should never disturb the header.
      console.warn("Notifications fetch failed:", err.message);
    } finally {
      setLoaded(true);
    }
  }, [request]);

  // Load now, then poll; also refresh when the tab becomes visible again.
  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  // Close when clicking outside
  useEffect(() => {
    const onClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Read = removed: drop it from the list and the red count right away.
  const handleRead = async (item) => {
    setItems((prev) => prev.filter((n) => n.id !== item.id));
    setUnread((c) => Math.max(0, c - 1));
    setOpen(false);
    if (item.source_type === "appointment") navigate("/appointment");
    try {
      await request(`/${item.id}/read`, { method: "PUT" });
    } catch (err) {
      load(); // put things back in sync if it failed
    }
  };

  const handleReadAll = async () => {
    setItems([]);
    setUnread(0);
    try {
      await request("/read-all", { method: "PUT" });
    } catch (err) {
      load();
    }
  };

  const badgeText = unread > 9 ? "9+" : String(unread);

  return (
    <div className="dropdown" ref={wrapRef}>
      <button
        type="button"
        className="btn btn-link text-white position-relative"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        title="Notifications"
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
          if (!open) load();
        }}
        style={{
          textDecoration: "none",
          padding: "4px 10px",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 1,
        }}
      >
        <BellIcon size={22} />
        {unread > 0 && (
          <span
            className="badge rounded-pill bg-danger"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              minWidth: 18,
              fontSize: 10,
              lineHeight: "14px",
              padding: "2px 5px",
            }}
          >
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          className="dropdown-menu dropdown-menu-end show p-0"
          style={{ width: 320, maxWidth: "90vw", overflow: "hidden" }}
        >
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{ borderBottom: "1px solid #eee" }}
          >
            <strong style={{ fontSize: 14 }}>Notifications</strong>
            {items.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ fontSize: 12, textDecoration: "none" }}
                onClick={handleReadAll}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {!loaded ? (
              <p className="text-muted text-center my-4" style={{ fontSize: 13 }}>
                Loading…
              </p>
            ) : items.length === 0 ? (
              <p className="text-muted text-center my-4" style={{ fontSize: 13 }}>
                No notifications
              </p>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleRead(item)}
                  className="d-block w-100 text-start border-0 bg-white px-3 py-2"
                  style={{ borderBottom: "1px solid #f2f2f2", cursor: "pointer" }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <span style={{ marginTop: 2, color: "#dc3545", display: "inline-flex" }}>
                      {item.source_type === "appointment" ? <CalendarIcon /> : <AlarmIcon />}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: "#555", whiteSpace: "normal" }}>
                        {item.message}
                      </div>
                      <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                        {timeAgo(item.created_at)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserNotificationBell;